import { currencyDigits, type Sale } from "@eaub/shared";

export const REPORT_TIMEZONE = "Australia/Brisbane";
export function salesDay(date: string | Date): string {
  const parts = new Intl.DateTimeFormat("en-AU", { timeZone: REPORT_TIMEZONE, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(date));
  const part = (key: string) => parts.find(p => p.type === key)!.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}
export function shiftDay(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00Z`); date.setUTCDate(date.getUTCDate() + days); return date.toISOString().slice(0, 10);
}
export type SalesPeriod = "today" | "7" | "30" | "month" | "all" | "custom";
export function salesRange(period: SalesPeriod, start: string, end: string, now = new Date()) {
  const today = salesDay(now);
  const fromDay = period === "today" ? today : period === "7" ? shiftDay(today, -6) : period === "30" ? shiftDay(today, -29) : period === "month" ? `${today.slice(0, 7)}-01` : start;
  const endDay = period === "custom" ? end : today;
  if (period !== "all" && (!/^\d{4}-\d{2}-\d{2}$/.test(fromDay) || !/^\d{4}-\d{2}-\d{2}$/.test(endDay) || fromDay > endDay)) throw new Error("Choose a start date on or before the end date.");
  const from = period === "all" ? "1970-01-01T00:00:00.000Z" : new Date(`${fromDay}T00:00:00+10:00`).toISOString();
  const to = new Date(Math.min(Date.parse(`${shiftDay(endDay, 1)}T00:00:00+10:00`), now.getTime())).toISOString();
  if (Date.parse(from) < 0 || Date.parse(from) >= Date.parse(to)) throw new Error("Choose a reporting range between 1970 and today.");
  return { from, to };
}
export function salesTotals(items: Sale[]) {
  return items.reduce((total, sale) => ({
    collected: total.collected + sale.collectedMinor,
    refunded: total.refunded + sale.refundedMinor,
    count: total.count + (["paid", "partially_refunded", "refunded"].includes(sale.status) ? 1 : 0),
  }), { collected: 0, refunded: 0, count: 0 });
}
export interface SalesBucket { key: string; label: string; stripe: number; square: number }
export function salesBuckets(items: Sale[], from: string, to: string): SalesBucket[] {
  const dates = items.map(sale => salesDay(sale.createdAt)).sort();
  const first = from.startsWith("1970") ? dates[0] || salesDay(to) : salesDay(from);
  const last = salesDay(new Date(Date.parse(to) - 1));
  const days = (Date.parse(last) - Date.parse(first)) / 86400000;
  const length = days <= 45 ? 10 : days <= 730 ? 7 : 4;
  const map = new Map<string, SalesBucket>();
  let current = first.slice(0, length), end = last.slice(0, length);
  while (current <= end) {
    const day = length === 10 ? current : length === 7 ? `${current}-01` : `${current}-01-01`;
    const label = new Intl.DateTimeFormat("en-AU", { timeZone: REPORT_TIMEZONE, ...(length === 10 ? { day: "numeric", month: "short" } : length === 7 ? { month: "short", year: "2-digit" } : { year: "numeric" }) }).format(new Date(`${day}T00:00:00+10:00`));
    map.set(current, { key: current, label, stripe: 0, square: 0 });
    current = length === 10 ? shiftDay(current, 1) : length === 7 ? shiftDay(`${current}-01`, 32).slice(0, 7) : String(Number(current) + 1);
  }
  for (const sale of items) {
    const bucket = map.get(salesDay(sale.createdAt).slice(0, length));
    if (bucket) bucket[sale.provider] += sale.collectedMinor;
  }
  return [...map.values()];
}

export function exportSales(items: Sale[], mode: string) {
  const cell = (value: unknown) => {
    const text = String(value ?? "");
    const formula = /^[=+@\-\t\r\n]/.test(text) || /^[=+@\-]/.test(text.trimStart());
    return `"${(formula ? "'" + text : text).replaceAll('"', '""')}"`;
  };
  const amount = (minor: number, currency: string) => (minor / 10 ** currencyDigits(currency)).toFixed(currencyDigits(currency));
  const rows: unknown[][] = [["Provider", "Mode", "Reference", "Created (UTC)", "Currency", "Requested", "Collected", "Refunded", "After refunds", "Status", "Disputed", "Customer", "Email", "Description", "Location", "Order reference"]];
  for (const sale of items) rows.push([sale.provider, mode, sale.reference, sale.createdAt, sale.currency,
    amount(sale.amountMinor, sale.currency), amount(sale.collectedMinor, sale.currency), amount(sale.refundedMinor, sale.currency),
    amount(sale.collectedMinor - sale.refundedMinor, sale.currency), sale.status, sale.disputed, sale.customer, sale.email, sale.description, sale.location, sale.orderId]);
  const blob = new Blob(["\uFEFF" + rows.map(row => row.map(cell).join(",")).join("\r\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob); const link = document.createElement("a");
  link.href = url; link.download = `eaub-sales-${mode}-${salesDay(new Date())}.csv`; link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
